import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import LanternSky from 'pages/ShareYourLight/components/LanternSky';
import { getShareLightPublicIds, getCsrfToken } from 'pages/ShareYourLight/api';
import CommunityRulesModal from 'pages/ShareYourLight/components/CommunityRulesModal';
import LightInDarknessModal from 'pages/ShareYourLight/components/LightInDarknessModal';
import ShareYourLightModal from 'pages/ShareYourLight/components/ShareYourLightModal';
import http from 'utils/http';
import { useQuery } from 'react-query';
import { handleApiError, trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName, LogoUrl } from 'utils/constants';


function ShareYourLight() {
  const [addMode, setAddMode] = useState<boolean>(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [thoughts, setThoughts] = useState<string[]>([]);
  const [likedLanterns, setLikedLanterns] = useState<{ [thoughtId: string]: boolean }>({});
  const [modalThoughtId, setModalThoughtId] = useState<string>('');

  React.useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_PAGE_LANDED, {
      referrer: document.referrer || 'direct',
    });
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tid = params.get('thoughtId');
    if (tid) setModalThoughtId(tid);
  }, []);


  const { isLoading: loading } = useQuery(
    ['share-light-public-ids'],
    async () => getShareLightPublicIds(http.get)(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setThoughts(data.submissionIds || []);
      },
      onError: (err) => {
        handleApiError(err, 'ShareYourLight: Failed to fetch public thought IDs');
      },
    },
  );

  // useQuery for CSRF token
  const {
    data: csrfData,
    refetch: refetchCsrfTokenQuery,
  } = useQuery('csrf-token', () => getCsrfToken(http.get)(), {
    refetchOnWindowFocus: false,
  });
  const csrfToken = csrfData?.['CSRF-Token'] || '';

  const refetchCsrfToken = async () => {
    const resp = await refetchCsrfTokenQuery();
    return resp.data?.['CSRF-Token'] || '';
  };

  return (
    <Box sx={{
      minHeight: '100dvh',
      width: '100vw',
      background: '#04121B',
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 10,
    }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh' }}>
          <CircularProgress color='inherit' />
        </Box>
      ) : (
        <LanternSky
          thoughts={thoughts}
          setAddMode={setAddMode}
          likedLanterns={likedLanterns}
          setLikedLanterns={setLikedLanterns}
          openThoughtId={modalThoughtId}
          setOpenThoughtId={setModalThoughtId}
          csrfToken={csrfToken}
          refetchCsrfToken={refetchCsrfToken}
        />
      )}
      <LightInDarknessModal />
      <ShareYourLightModal
        open={addMode}
        onClose={() => setAddMode(false)}
        csrfToken={csrfToken}
        refetchCsrfToken={refetchCsrfToken}
      />
      <CommunityRulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} />
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        width: '100%',
        textAlign: 'center',
        maxWidth: 550,
        left: '50%',
        transform: 'translateX(-50%)',
        alignSelf: 'center',
        zIndex: 20,
      }}
      >
        <Box sx={{ borderRadius: 2, p: 1, background: 'rgba(4, 18, 27, 0.60);' }}>
          <Typography variant='h6' fontWeight={700} fontSize={12} gutterBottom color='white' mb={2}>
            In recognition of suicide prevention awareness month
          </Typography>
          <Typography variant='body1' display='block' gutterBottom color='white' fontSize={12}>
            This is not a substitute for professional medical advice. If you are in crisis,
            please call or text 988 (U.S. Suicide and Crisis Lifeline)
            or your local emergency number.
            Conversations here are peer support only.
            They are not therapy, treatment, or professional counseling.
          </Typography>
          <Typography
            variant='body1'
            display='block'
            fontSize={14}
            sx={{
              mt: 3.5,
              mb: 1,
              cursor: 'pointer',
              color: '#FED892',
              ':hover': {
                textDecoration: 'underline',
              } }}
            onClick={() => {
              trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_COMMUNITY_RULES_LINK_CLICKED);
              setRulesOpen(true);
            }}
          >
            Community Rules
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'absolute', right: 16, bottom: 16, zIndex: 21 }}>
        <input
          type='image'
          height={25}
          width={100}
          src={LogoUrl.lgLight}
          alt='Logo'
          onClick={() => {
            trackMixpanelEvent(MixpanelEventName.SHAREYOURLIGHT_LOGO_IMAGE_CLICKED);
            window.open('https://www.headlamp.com', '_blank');
          }}
        />
      </Box>
    </Box>
  );
}

export default ShareYourLight;
